// mantine
import { Button, Select, TextInput, UnstyledButton } from '@mantine/core';
// utils
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import type { IFlowTemplateData } from '@newo/api/gateway';
import { useFlowTemplatesQuery } from '@/services/designer/templates/templates.queries.ts';
import validationSchema from '@/pages/Agents/components/AddFlowForm/AddFlowForm.schema.ts';
import {
    useCreateEmptyFlowMutation,
    useCreateTemplateFlowMutation
} from '@/services/designer/flow/flow/flow.queries.ts';

interface AgentFlowFormProps {
    agentId: string;
    onCancel?: () => void;
    onClose?: () => void;
    onSubmit: () => void;
}

const AddFlowForm = ({
    agentId,
    onCancel,
    onClose,
    onSubmit
}: AgentFlowFormProps) => {
    const { data: templates = [] } = useFlowTemplatesQuery();
    const createEmptyFlowMutation = useCreateEmptyFlowMutation();
    const createTemplateFlowMutation = useCreateTemplateFlowMutation();
    const initialValues: IFlowTemplateData = {
        idn: '',
        title: '',
        template_flow_idn: ''
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(formData);
            if (data.template_flow_idn) {
                createTemplateFlowMutation.mutate(
                    {
                        agentId,
                        data
                    },
                    {
                        onSuccess: () => {
                            onSubmit();
                            onClose?.();
                        },
                        onSettled: () => {
                            formik.setSubmitting(false);
                        }
                    }
                );
            } else {
                const { idn, title } = data;
                createEmptyFlowMutation.mutate(
                    {
                        agentId,
                        data: { idn, title }
                    },
                    {
                        onSuccess: () => {
                            onSubmit();
                            onClose?.();
                        },
                        onSettled: () => {
                            formik.setSubmitting(false);
                        }
                    }
                );
            }
        }
    });

    const handleCancel = () => {
        onCancel?.();
        onClose?.();
    };

    const templateOptions = templates.map((template) => ({
        value: template.idn,
        label: template.title
    }));

    const handleTemplateChange = (value: string | null) => {
        formik.setFieldValue('template_flow_idn', value);
    };

    const handleTemplateClear = () => {
        formik.setFieldValue('template_flow_idn', '');
    };

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            <Select
                clearable
                clearButtonProps={{ onClick: handleTemplateClear }}
                comboboxProps={{ withinPortal: false }}
                data={templateOptions}
                label="Flow template"
                placeholder="Flow template"
                onChange={handleTemplateChange}
            />
            <TextInput
                withAsterisk
                description="Unique Identifying Name"
                error={
                    formik.touched.idn && formik.values.idn
                        ? formik.errors.idn
                        : undefined
                }
                label="Flow Idn"
                name="idn"
                placeholder="Flow Idn"
                value={formik.values.idn}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />
            <TextInput
                withAsterisk
                error={
                    formik.touched.title && formik.values.title
                        ? formik.errors.title
                        : undefined
                }
                label="Flow Title"
                name="title"
                placeholder="Title"
                value={formik.values.title}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
            />

            <div className="flex justify-between gap-2">
                <UnstyledButton
                    className="text-xs text-gray-6"
                    onClick={handleCancel}
                >
                    Cancel
                </UnstyledButton>
                <Button
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Create
                </Button>
            </div>
        </form>
    );
};

export default AddFlowForm;
